// Build and download an iCalendar (.ics) file for a study plan + exam date.
// Spec: RFC 5545. We emit one VEVENT per study task plus one all-day VEVENT for the exam.

interface StudyTask {
  id: string;
  scheduled_date: string; // YYYY-MM-DD
  task_type?: string | null;
  description?: string | null;
  duration_minutes?: number | null;
  topic_name?: string | null;
}

interface StudyWeek {
  week_number?: number;
  study_plan_tasks?: StudyTask[] | null;
}

interface StudyPlan {
  id: string;
  exam_date: string; // YYYY-MM-DD
  exam_slug?: string | null;
  study_plan_weeks?: StudyWeek[] | null;
}

const escape = (s: string) =>
  String(s)
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const fmtDate = (ymd: string) => ymd.replace(/-/g, ''); // 20260901
const fmtUtc = (d: Date) =>
  d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

// Fold long lines per RFC 5545 (max 75 octets, continuation begins with a space).
const fold = (line: string): string => {
  if (line.length <= 73) return line;
  const out: string[] = [];
  let rest = line;
  out.push(rest.slice(0, 73));
  rest = rest.slice(73);
  while (rest.length > 72) {
    out.push(' ' + rest.slice(0, 72));
    rest = rest.slice(72);
  }
  if (rest.length) out.push(' ' + rest);
  return out.join('\r\n');
};

export function buildStudyPlanICS(plan: StudyPlan, examLabel?: string): string {
  const now = fmtUtc(new Date());
  const prodId = '-//QuickMedicalReview//Study Plan//EN';
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:${prodId}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escape(`Study Plan${examLabel ? ` — ${examLabel}` : ''}`)}`,
    'X-WR-TIMEZONE:UTC',
  ];

  // Exam date — all-day event
  if (plan.exam_date) {
    const start = fmtDate(plan.exam_date);
    const endDate = new Date(plan.exam_date + 'T00:00:00Z');
    endDate.setUTCDate(endDate.getUTCDate() + 1);
    const end = endDate.toISOString().slice(0, 10).replace(/-/g, '');
    lines.push(
      'BEGIN:VEVENT',
      `UID:exam-${plan.id}@quickmedicalreview`,
      `DTSTAMP:${now}`,
      `DTSTART;VALUE=DATE:${start}`,
      `DTEND;VALUE=DATE:${end}`,
      fold(`SUMMARY:🎯 Exam Day${examLabel ? ` — ${examLabel}` : ''}`),
      fold('DESCRIPTION:Your scheduled exam date. Good luck!'),
      'CATEGORIES:Exam',
      'BEGIN:VALARM',
      'TRIGGER:-P7D',
      'ACTION:DISPLAY',
      'DESCRIPTION:Exam in 1 week',
      'END:VALARM',
      'END:VEVENT'
    );
  }

  // Study tasks — all-day per scheduled date (most plans don't have specific times)
  for (const week of plan.study_plan_weeks || []) {
    for (const task of week.study_plan_tasks || []) {
      if (!task.scheduled_date) continue;
      const start = fmtDate(task.scheduled_date);
      const endDate = new Date(task.scheduled_date + 'T00:00:00Z');
      endDate.setUTCDate(endDate.getUTCDate() + 1);
      const end = endDate.toISOString().slice(0, 10).replace(/-/g, '');

      const summaryParts: string[] = [];
      if (task.task_type) summaryParts.push(humanType(task.task_type));
      if (task.topic_name) summaryParts.push(task.topic_name);
      const summary = summaryParts.join(': ') || task.description || 'Study task';

      const descParts: string[] = [];
      if (task.description) descParts.push(task.description);
      if (task.duration_minutes) descParts.push(`Estimated time: ${task.duration_minutes} min`);
      if (week.week_number) descParts.push(`Week ${week.week_number}`);
      const description = descParts.join('\n');

      lines.push(
        'BEGIN:VEVENT',
        `UID:task-${task.id}@quickmedicalreview`,
        `DTSTAMP:${now}`,
        `DTSTART;VALUE=DATE:${start}`,
        `DTEND;VALUE=DATE:${end}`,
        fold(`SUMMARY:📚 ${escape(summary)}`),
        fold(`DESCRIPTION:${escape(description)}`),
        'CATEGORIES:Study'
      );
      lines.push('END:VEVENT');
    }
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

function humanType(t: string): string {
  const map: Record<string, string> = {
    practice: 'Practice',
    review: 'Review',
    mock_exam: 'Mock Exam',
    notes: 'Notes',
    flashcards: 'Flashcards',
  };
  return map[t] || t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function downloadICS(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
